import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  clearOrgSelectionRequired,
  clearPendingSignupOrganizationId,
  getPendingSignupOrganizationId,
  POST_AUTH_PATH,
} from '@/lib/auth/org-selection';
import {
  excludeDefaultOrganizations,
  isDefaultOrganizationId,
  isOrganizationEnabled,
  mustSelectNonDefaultOrganization,
} from '@/lib/auth/org-access';
import { useListOrganizations } from '@/lib/api/hooks/use-multi-orgs';
import { useAssignToOrganization } from '@/hooks/use-assign-to-organization';
import { useSignoutMutation } from '@/modules/auth/hooks/use-auth';
import { useAuthStore } from '@/state/store/auth';
import { LOGIN_COLORS } from '../../constants/login-theme';
import { cn } from '@/lib/utils';

/**
 * Full-page organization pick after self-signup when the user is still on Default.
 * Skips straight to contacts when membership / session is already a non-Default org.
 */
export const SelectOrganizationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const { logout } = useAuthStore();
  const { mutateAsync: signout, isPending: isSigningOut } = useSignoutMutation();
  const { account, assignAndSwitch, isBusy } = useAssignToOrganization();
  const {
    data: orgsData,
    isLoading,
    isError,
  } = useListOrganizations({ page: 0, pageSize: 10 });

  const availableOrganizations = useMemo(
    () =>
      excludeDefaultOrganizations(
        (orgsData?.organizations ?? []).filter(isOrganizationEnabled)
      ),
    [orgsData?.organizations]
  );

  const [pickedId, setPickedId] = useState<string | null>(null);

  // Skip when session org or memberships are already non-Default
  useEffect(() => {
    const sessionIsNonDefault =
      Boolean(selectedOrgId) && !isDefaultOrganizationId(selectedOrgId);

    if (account) {
      if (mustSelectNonDefaultOrganization(account, selectedOrgId)) return;
    } else if (!sessionIsNonDefault) {
      return;
    }

    clearOrgSelectionRequired();
    clearPendingSignupOrganizationId();
    navigate(POST_AUTH_PATH, { replace: true });
  }, [account, navigate, selectedOrgId]);

  useEffect(() => {
    if (pickedId || availableOrganizations.length === 0) return;
    const pendingId = getPendingSignupOrganizationId();
    if (pendingId && availableOrganizations.some((org) => org.itemId === pendingId)) {
      setPickedId(pendingId);
    }
  }, [availableOrganizations, pickedId]);

  const isWorking = isBusy || isSigningOut;
  const canContinue = Boolean(pickedId) && !isWorking && !isLoading && !isError;

  const helperCopy = useMemo(() => {
    if (isLoading) return t('LOADING') || 'Loading organizations…';
    if (isError) {
      return 'Could not load organizations. Try again, or log out to use a different account.';
    }
    if (availableOrganizations.length === 0) {
      return 'No organizations are available. Contact an administrator, or log out.';
    }
    return 'Select an organization to continue. Default organization cannot be used.';
  }, [availableOrganizations.length, isError, isLoading, t]);

  const onContinue = async () => {
    if (!pickedId || isWorking) return;
    await assignAndSwitch(pickedId, { redirectTo: POST_AUTH_PATH });
  };

  const handleLogout = async () => {
    try {
      await signout();
    } catch {
      // Still clear local session so the user is not stuck on this step.
    } finally {
      clearOrgSelectionRequired();
      clearPendingSignupOrganizationId();
      logout();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <h2
          className="text-[2rem] font-bold leading-tight tracking-tight sm:text-[2.125rem]"
          style={{ color: LOGIN_COLORS.textPrimary }}
        >
          Select organization
        </h2>
        <p className="text-sm font-normal leading-5" style={{ color: LOGIN_COLORS.textMuted }}>
          {helperCopy}
        </p>
      </div>

      <div className="flex max-h-[320px] flex-col gap-1 overflow-y-auto rounded-xl border border-neutral-200 p-2">
        {isLoading ? (
          <p className="px-3 py-8 text-center text-sm text-neutral-500">Loading…</p>
        ) : isError ? (
          <p className="px-3 py-8 text-center text-sm text-destructive">
            Failed to load organizations.
          </p>
        ) : availableOrganizations.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-neutral-500">No organizations found.</p>
        ) : (
          availableOrganizations.map((org) => {
            const isSelected = org.itemId === pickedId;
            return (
              <button
                key={org.itemId}
                type="button"
                disabled={isWorking}
                onClick={() => setPickedId(org.itemId)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm transition-colors',
                  isSelected
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-neutral-900 hover:bg-neutral-50'
                )}
              >
                <span className="min-w-0 flex-1 truncate font-medium">{org.name}</span>
                <span className="flex size-5 shrink-0 items-center justify-center">
                  {isSelected ? <CheckIcon className="size-4 text-primary-600" aria-hidden /> : null}
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          className="h-12 w-full rounded-xl border-0 text-sm font-semibold shadow-none hover:opacity-90"
          size="lg"
          disabled={!canContinue}
          loading={isBusy}
          onClick={() => void onContinue()}
          style={{ backgroundColor: LOGIN_COLORS.primary, color: LOGIN_COLORS.pureWhite }}
        >
          Continue
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-11 w-full rounded-xl text-sm font-medium"
          disabled={isWorking}
          loading={isSigningOut}
          onClick={() => void handleLogout()}
        >
          {t('LOG_OUT') || 'Log out'}
        </Button>
      </div>
    </div>
  );
};
